import "./surveys.css";
import { useEffect, useMemo, useRef, useState } from "react";
import SignatureCanvas from "react-signature-canvas";
import { isAnswered as answered } from "../appraisals/appraisalHelpers";

/**
 * The questions of a survey, answered by the person surveyed. Used on the public link page
 * (customers, suppliers, employees from their email), in the app's My surveys, and for our
 * side of a two-way survey. Plain elements and its own styles, so it works where the app's
 * styles aren't loaded.
 */
/** The input for one question (rating, yes / no, checkbox, choice, number, date, short or written answer). */
export const QuestionField = ({ q, a = {}, scale, set }) => (
    <>
                                {q.type === "rating" && (
                                    <div className="svy-options">
                                        {Array.from({ length: scale?.max || 5 }, (_, i) => i + 1).map((n) => (
                                            <button type="button" key={n} className={`svy-option${a.rating === n ? " selected" : ""}`} onClick={() => set({ rating: n })}>
                                                <span className="svy-option-num">{n}</span>{scale?.labels?.[n - 1] || ""}
                                            </button>
                                        ))}
                                    </div>
                                )}
                                {q.type === "yesno" && (
                                    <div className="svy-options">
                                        {[true, false].map((v) => (
                                            <button type="button" key={String(v)} className={`svy-option${a.answer === v ? " selected" : ""}`} onClick={() => set({ answer: v })}>{v ? "Yes" : "No"}</button>
                                        ))}
                                    </div>
                                )}
                                {q.type === "comment" && (
                                    <textarea className="svy-textarea" value={a.comment || ""} placeholder="Your answer" maxLength={4000} onChange={(e) => set({ comment: e.target.value })} />
                                )}
                                {q.type === "text" && (
                                    <input className="svy-input" value={a.comment || ""} placeholder="Your answer" maxLength={500} onChange={(e) => set({ comment: e.target.value })} />
                                )}
                                {q.type === "number" && (
                                    <input className="svy-input" type="number" inputMode="decimal" style={{ maxWidth: 200 }} value={a.number ?? ""} placeholder="0"
                                        onChange={(e) => set({ number: e.target.value === "" ? undefined : Number(e.target.value) })} />
                                )}
                                {q.type === "date" && (
                                    <input className="svy-input" type="date" style={{ maxWidth: 220 }} value={a.date || ""} onChange={(e) => set({ date: e.target.value || undefined })} />
                                )}
                                {q.type === "checkbox" && (
                                    <label className={`svy-check${a.answer === true ? " on" : ""}`} data-check={q.id}>
                                        <input type="checkbox" checked={a.answer === true} onChange={(e) => set({ answer: e.target.checked })} />
                                        <span className="svy-check-box" aria-hidden="true">{a.answer === true ? "✓" : ""}</span>
                                        <span className="svy-check-label">{q.text}{q.required !== false && <span className="svy-required">*</span>}</span>
                                    </label>
                                )}
                                {q.type === "choice" && ((q.options || []).length <= 6 ? (
                                    <div className="svy-options">
                                        {q.options.map((option) => (
                                            <button type="button" key={option} className={`svy-option${a.choice === option ? " selected" : ""}`} onClick={() => set({ choice: option })}>{option}</button>
                                        ))}
                                    </div>
                                ) : (
                                    <select className="svy-input" style={{ maxWidth: 360 }} value={a.choice || ""} onChange={(e) => set({ choice: e.target.value || undefined })}>
                                        <option value="">Choose...</option>
                                        {q.options.map((option) => <option key={option} value={option}>{option}</option>)}
                                    </select>
                                ))}
    </>
);

// "Sign here": draw with a finger / mouse, or type a full name instead. The pad is sized to its
// box so the strokes land where they're drawn on any screen.
export const SignHere = ({ onChange, drawOnly = false }) => {
    const box = useRef(null);
    const pad = useRef(null);
    const [width, setWidth] = useState(0);
    const [typed, setTyped] = useState("");
    const [drawn, setDrawn] = useState(false);
    useEffect(() => { if (box.current) setWidth(Math.floor(box.current.clientWidth)); }, []);
    const report = (isDrawn, name) => {
        if (isDrawn) {
            let canvas;
            try { canvas = pad.current.getTrimmedCanvas(); } catch { canvas = pad.current.getCanvas(); }
            onChange({ signature: canvas.toDataURL("image/png") });
        } else onChange(name.trim().length >= 2 ? { typedName: name.trim() } : null);
    };
    return (
        <div className="svy-section" data-sign>
            <div className="svy-section-title">Sign</div>
            <div className="svy-hint" style={{ marginBottom: 8 }}>{drawOnly ? "Draw your signature - it goes on the document." : "Your signature goes on the form."}</div>
            <div className="svy-sign-pad" ref={box}>
                {width > 0 && <SignatureCanvas ref={pad} penColor="#111827" canvasProps={{ width, height: 150, className: "svy-sign-canvas" }}
                    onEnd={() => { const isDrawn = !pad.current.isEmpty(); setDrawn(isDrawn); report(isDrawn, typed); }} />}
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", margin: "4px 0 10px" }}>
                <span className="svy-hint">{drawn ? "Signed" : "Sign in the box"}</span>
                <button type="button" className="svy-clear" onClick={() => { pad.current?.clear(); setDrawn(false); report(false, typed); }}>Clear</button>
            </div>
            {!drawOnly && (
                <input className="svy-input" placeholder="Or type your full name" value={typed} maxLength={120}
                    onChange={(e) => { setTyped(e.target.value); if (!drawn) report(false, e.target.value); }} />
            )}
        </div>
    );
};

export const SurveyAnswerForm = ({ form, submitting = false, onSubmit, submitLabel = "Send my answers" }) => {
    const [answers, setAnswers] = useState({});
    const needsSignature = form?.signature === "required";
    const [signed, setSigned] = useState(null);
    const scale = form?.template?.ratingScale;
    const questions = useMemo(() => (form?.template?.sections || []).flatMap((s) => s.questions), [form]);
    const missing = questions.filter((q) => q.required !== false && !answered(q, answers[q.id]));
    const set = (id, patch) => setAnswers((prev) => ({ ...prev, [id]: { ...(prev[id] || {}), ...patch } }));

    return (
        <div className="svy-form">
            {form?.anonymous && <div className="svy-note">Your answers are anonymous - nobody can see which answers are yours.</div>}
            {form?.formPdfUrl && (
                <div className="svy-hint" style={{ marginBottom: 14 }}>
                    This is the {form.companyName ? `${form.companyName} ` : ""}form - your answers are written into it. <a href={form.formPdfUrl} target="_blank" rel="noreferrer">See the form</a>
                </div>
            )}
            {(form?.template?.sections || []).map((section) => (
                <div className="svy-section" key={section.id}>
                    {section.title && <div className="svy-section-title">{section.title}</div>}
                    {section.questions.map((q) => {
                        const a = answers[q.id] || {};
                        return (
                            <div className="svy-question" key={q.id} data-question={q.id}>
                                {q.type !== "checkbox" && <div className="svy-question-text">{q.text}{q.required !== false && <span className="svy-required">*</span>}</div>}
                                <QuestionField q={q} a={a} scale={scale} set={(patch) => set(q.id, patch)} />
                            </div>
                        );
                    })}
                </div>
            ))}
            {needsSignature && <SignHere onChange={setSigned} />}
            <div className="svy-submit-row">
                <button type="button" className="svy-submit" disabled={submitting || missing.length > 0 || (needsSignature && !signed)} onClick={() => onSubmit(answers, needsSignature ? signed : undefined)}>
                    {submitting ? "Sending..." : submitLabel}
                </button>
                {missing.length > 0 ? <span className="svy-hint">{missing.length} {missing.length === 1 ? "question" : "questions"} still to answer</span>
                    : needsSignature && !signed ? <span className="svy-hint">Sign (or type your name) to send</span> : null}
            </div>
        </div>
    );
};
